import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ListingForm from "@/components/listings/ListingForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Coins, InfoIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import UnauthenticatedFeaturesDialog from "@/components/listings/UnauthenticatedFeaturesDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";

interface UserCreditsRpcResponse {
  free_listings_remaining?: number;
  purchased_listings_credits?: number;
  default_free_listings?: number;
}

const CreateListing = () => {
  const navigate = useNavigate();
  const { profile, isAuthenticated } = useAuth();
  const [showCoinDialog, setShowCoinDialog] = useState(false);
  const [isCheckingCredits, setIsCheckingCredits] = useState(true);
  const [showApprovalInfo, setShowApprovalInfo] = useState(true);
  const [showUnauthenticatedInfo, setShowUnauthenticatedInfo] = useState(!isAuthenticated);
  const [guestUserId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (isAuthenticated && profile?.id) {
      const checkInitialCredits = async () => {
        setIsCheckingCredits(true);
        try {
          const { data: rpcData, error: rpcError } = await supabase.rpc(
            "get_user_credits",
            { p_user_id: profile.id }
          );

          if (rpcError) throw rpcError;

          const credits = rpcData?.[0] as UserCreditsRpcResponse | undefined;
          const free = credits?.free_listings_remaining ?? 0;
          const purchased = credits?.purchased_listings_credits ?? 0;

          if (free + purchased <= 0) {
            setShowCoinDialog(true);
          }
        } catch (error) {
          console.error("CreateListing: Error checking initial credits:", error)
          toast({
            description: "Gabim gjatë kontrollimit të krediteve.",
            variant: "destructive",
          });
        } finally {
          setIsCheckingCredits(false);
        }
      };
      checkInitialCredits();
    } else {
      setIsCheckingCredits(false);
    }
  }, [isAuthenticated, profile?.id]);

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="container mx-auto px-4 py-8 flex-grow pt-20">
        <h1 className="text-2xl font-bold mb-4 dark:text-white">
          Krijo Shpallje të Re
        </h1>

        {isCheckingCredits ? (
           <div className="flex justify-center items-center h-64">
             <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
             <p className="ml-2">Duke kontrolluar kreditet...</p>
           </div>
        ) : (
          <ListingForm
            userId={isAuthenticated && profile?.id ? profile.id : guestUserId}
            isAuthenticated={isAuthenticated}
            defaultListingData={{
              is_active: false,
              approval_status: "pending",
              is_authenticated_listing: isAuthenticated,
            }}
          />
        )}

        <Dialog open={showCoinDialog && isAuthenticated} onOpenChange={setShowCoinDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuk ka kredite të disponueshme</DialogTitle>
              <DialogDescription>
                Ju nuk keni më kredite në dispozicion. Për të krijuar njoftime
                të reja, duhet të blini kredite.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 flex justify-center">
              <Coins className="h-16 w-16 text-amber-500" />
            </div>
            <DialogFooter className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCoinDialog(false)}
              >
                Mbyll
              </Button>
              <Button onClick={() => navigate("/coins")}>Blini kredite</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showApprovalInfo && (isAuthenticated || !showUnauthenticatedInfo)}
          onOpenChange={setShowApprovalInfo}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Informacion i rëndësishëm</DialogTitle>
              <DialogDescription>
                Te gjitha shpalljet e reja kërkojnë miratim nga një
                administrator para se të jenë të dukshme për përdoruesit e
                tjerë. Ky proces mund të marrë pak kohë. Ju do të njoftoheni
                sapo shpallja juaj të miratohet.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 flex justify-center">
              <InfoIcon className="h-16 w-16 text-blue-500" />
            </div>
            <DialogFooter>
              <Button onClick={() => setShowApprovalInfo(false)}>Pranoj</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <UnauthenticatedFeaturesDialog
          isOpen={showUnauthenticatedInfo}
          onClose={() => setShowUnauthenticatedInfo(false)}
        />
      </main>
      <Footer />
    </div>
  );
};

export default CreateListing;
